import jsPDF from 'jspdf';

import { customEval, mString } from '../utility';

import { GAP, LANDSCAPE_MAX_COLUMNS, PORTRAIT_MAX_COLUMNS } from './lib/constants';
import extractDataFromColDefs from './lib/extractDataFromColDefs';
import { addNestedTable, generateCompleteTable } from './lib/generateCompleteTable';
import { getAgGridData } from './lib/getAgGridData';
import { filterColumns } from './lib/helpers';
import { printPrintWindow } from './lib/printPrintWindow';
import { generateFooter } from './pdfFooter';
import { generateHeader } from './pdfHeader';

const getTotalsLine = (rows, columns, statusPanelData) => {
  if (!statusPanelData.length) {
    return [];
  }

  const mappedAmounts = Object.fromEntries(
    statusPanelData.map(({ field, value }) => [field, value])
  );

  const totalsLine = columns.map((col) => {
    if (Object.keys(mappedAmounts).includes(col.field)) {
      let amountInCents = 0;

      //if value is in statuses show that value
      if (mappedAmounts?.[col?.field] !== undefined) {
        amountInCents = mappedAmounts[col.field];
      } else {
        amountInCents =
          rows?.reduce((acc, curr) => {
            let value = Number(customEval(curr, col.field) || 0);

            if (col?.valueGetter) {
              value = col.valueGetter({ value, data: curr });
            }

            acc += Number(value);

            return acc;
          }, 0) || 0;
      }

      return mString(amountInCents);
    }

    return '';
  });

  if (totalsLine.length) {
    totalsLine[0] = 'Totals';
  }

  return totalsLine;
};

//////////////////////////////////////////// --- AG GRID TABLE
//get the data from the ag-grid
const getRowsAndTotals = (gridApi, selectedColumns = []) => {
  const { data, columns } = getAgGridData(gridApi);

  const finalColumns = selectedColumns?.length
    ? columns.filter((col) => selectedColumns.includes(col?.field))
    : columns;

  const exportRows = data.map((row) => {
    return finalColumns.map((col) => {
      return { content: row[col.field], strike: row.hasStrike };
    });
  });

  return { data, rows: exportRows, columns: finalColumns };
};

//print the ag-grid table
export const printAgGridTable = ({
  hasMultiple = false,
  statusPanelData = false,
  gridApi: proppedGridApi,
  headerConfigs: proppedHeaderConfigs = {},
  footerConfigs: proppedFooterConfigs = {},
  selectedColumns,
  maxColumns
}) => {
  if (!proppedGridApi) return;

  const doc = new jsPDF();
  let totalsSum;

  const createGridTablePdf = (
    gridApi = proppedGridApi,
    headerConfigs = proppedHeaderConfigs,
    footerConfigs = proppedFooterConfigs
  ) => {
    const { data, rows, columns } = getRowsAndTotals(gridApi, selectedColumns);

    if (statusPanelData) {
      totalsSum = getTotalsLine(data, columns, statusPanelData);
    }

    generateCompleteTable({
      doc,
      columnHeaders: columns.map((col) => col.headerName),
      rows,
      totalsSum,
      headerConfigs,
      footerConfigs,
      maxColumns
    });
  };

  if (!hasMultiple) {
    createGridTablePdf();
  } else {
    // this case is used in preview multiple invoices
    const gridApiEntries = Object.entries(proppedGridApi);

    gridApiEntries.forEach(([key, value], index) => {
      createGridTablePdf(value, proppedHeaderConfigs?.[key], proppedFooterConfigs?.[key]);

      //add page if not last page
      if (index < gridApiEntries.length - 1) {
        doc.addPage();
      }
    });
  }

  printPrintWindow(doc);
};
//////////////////////////////////////////// ^^^ AG GRID TABLE

//////////////////////////////////////////// --- CUSTOM TABLE

//print table when data are collapsed
const printCollapsedTable = ({
  doc,
  category,
  tableData,
  columns,
  orientation,
  maxColumns,
  statusPanelData,
  headerConfigs,
  footerConfigs
}) => {
  let totalsSum;

  const formattedData = extractDataFromColDefs(tableData, columns);

  const rows = formattedData.map((row) => columns.map((col) => row[col.field]));
  const columnHeaders = columns.map((col) => col.headerName);

  if (statusPanelData) {
    totalsSum = getTotalsLine(tableData, columns, statusPanelData);
  }

  generateCompleteTable({
    doc,
    category,
    columnHeaders,
    rows,
    orientation,
    maxColumns,
    totalsSum,
    headerConfigs,
    footerConfigs
  });
};

//print table when data are expanded
const printExpandedTable = ({ doc, tableData, columns, headerConfigs, footerConfigs }) => {
  const startY = generateHeader({ doc, headerConfigs });

  const endY = addNestedTable({ doc, data: tableData, columns, startY: startY + GAP / 2 });

  generateFooter({ doc, endY, headerConfigs, footerConfigs });
};

//print the table which is not ag-grid
export const printTable = ({
  category,
  tableData,
  columns: proppedColumns,
  filterHiddenColumns = true,
  orientation = 'portrait',
  maxColumns,
  statusPanelData = false,
  printExpandedRows = false,
  headerConfigs = {},
  footerConfigs = {}
}) => {
  if (!tableData || !proppedColumns) return;

  const doc = new jsPDF({ orientation });

  const columns = filterHiddenColumns ? filterColumns(proppedColumns) : proppedColumns;

  const finalMaxColumns =
    maxColumns || (orientation === 'portrait' ? PORTRAIT_MAX_COLUMNS : LANDSCAPE_MAX_COLUMNS);

  if (printExpandedRows) {
    printExpandedTable({
      doc,
      tableData,
      columns,
      maxColumns: finalMaxColumns,
      statusPanelData,
      headerConfigs,
      footerConfigs
    });
  } else {
    printCollapsedTable({
      doc,
      category,
      tableData,
      columns,
      orientation,
      maxColumns: finalMaxColumns,
      statusPanelData,
      headerConfigs,
      footerConfigs
    });
  }

  printPrintWindow(doc);
};
